import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../utils/supabase';
import { getConversationUrl } from '../utils/conversationUrlHelpers';
import Toast from '../components/ui/Toast';

const ContactVAForm = () => {
  const { id } = useParams();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [vaProfile, setVaProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    // Redirect to login if not logged in
    if (!user && !loading) {
      setToast({ show: true, message: 'You must be logged in to contact a VA', type: 'error' });
      navigate('/login', { state: { from: `/contact-va/${id}` } });
      return;
    }

    // Only employers should be contacting VAs
    if (userProfile && userProfile.role !== 'employer' && !loading) {
      setToast({ show: true, message: 'Only employers can contact VAs directly', type: 'error' });
      navigate('/');
      return;
    }

    const fetchVAProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, profile_picture')
          .eq('id', id)
          .eq('role', 'va')
          .single();

        if (error) throw error;
        if (!data) throw new Error('VA not found');
        
        setVaProfile(data);
      } catch (err) {
        console.error('Error fetching VA profile:', err);
        setToast({ show: true, message: 'Could not find VA profile', type: 'error' });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchVAProfile();
  }, [id, user, userProfile, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      setToast({ show: true, message: 'Please fill in all fields', type: 'error' });
      return;
    }

    setSending(true);
    try {
      // Create a conversation if one doesn't exist
      const { data: existingConversation, error: conversationError } = await supabase
        .from('conversations')
        .select('id')
        .or(`(sender_id.eq.${user.id},receiver_id.eq.${user.id})`)
        .or(`(sender_id.eq.${vaProfile.id},receiver_id.eq.${vaProfile.id})`)
        .maybeSingle();
      
      if (conversationError && conversationError.code !== 'PGRST116') {
        throw conversationError;
      }
      
      let conversationId;
      
      if (!existingConversation) {
        // Create new conversation
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            sender_id: user.id,
            receiver_id: vaProfile.id,
            last_message: message,
            last_message_time: new Date().toISOString()
          })
          .select('id')
          .single();
          
        if (createError) throw createError;
        conversationId = newConversation.id;
      } else {
        conversationId = existingConversation.id;
        
        // Update last message
        await supabase
          .from('conversations')
          .update({
            last_message: message,
            last_message_time: new Date().toISOString()
          })
          .eq('id', conversationId);
      }
      
      // Save the message
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          recipient_id: vaProfile.id,
          content: message,
          subject: subject
        });
        
      if (messageError) throw messageError;
      
      // Create a notification for the VA
      await supabase
        .from('notifications')
        .insert({
          user_id: vaProfile.id,
          type: 'message',
          content: `New message from ${userProfile.full_name}: ${subject}`,
          related_id: conversationId,
          is_read: false
        });
      
      setToast({ show: true, message: 'Message sent successfully', type: 'success' });
      
      // Navigate to secure conversation URL
      const conversationUrl = getConversationUrl(vaProfile);
      navigate(conversationUrl);
    } catch (err) {
      console.error('Error sending message:', err);
      setToast({ show: true, message: 'Failed to send message', type: 'error' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="animate-pulse">
          <div className="bg-gray-200 h-8 w-1/3 rounded mb-4"></div>
          <div className="bg-gray-200 h-4 rounded mb-2.5"></div>
          <div className="bg-gray-200 h-4 rounded mb-2.5"></div>
          <div className="bg-gray-200 h-4 rounded mb-2.5"></div>
          <div className="bg-gray-200 h-4 w-1/2 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">Contact {vaProfile.full_name}</h1>
          
          <div className="flex items-center mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex-shrink-0">
              {vaProfile.profile_picture ? (
                <img 
                  src={vaProfile.profile_picture} 
                  alt={`${vaProfile.full_name}`}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-2xl text-gray-500">{vaProfile.full_name.charAt(0)}</span>
                </div>
              )}
            </div>
            <div className="ml-4">
              <h2 className="text-lg font-semibold">{vaProfile.full_name}</h2>
              <p className="text-gray-600 text-sm">
                Your message will initiate a direct conversation with this VA.
              </p>
            </div>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., Job Opportunity from Company Name"
                required
                maxLength={100}
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md w-full"
                placeholder="Describe the opportunity, your company, and why you'd like to connect with this VA."
                rows={6}
                required
                maxLength={2000}
              ></textarea>
              <div className="text-right text-xs text-gray-500 mt-1">
                {message.length}/2000 characters
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 mr-3"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                disabled={sending || !subject.trim() || !message.trim()}
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactVAForm;


